// bluaIQ — Calendar Sync Module
// .ics download + Google/Apple Calendar subscription feeds.
// This is the "sync to phone" side of the Calendar — the actual Month/Week/Day
// visualization lives in CalendarModule.tsx.

import { apiFetch } from '../utils/api'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CalendarDays, Download, Link2, Copy, Check, Smartphone, ExternalLink, Trash2 } from 'lucide-react'
import { isDemoProject } from '../utils/demoData'

// ── TYPES ─────────────────────────────────────────

interface CalendarSubscription {
  id: string
  token: string
  platform?: string
  isActive: boolean
  createdAt: string
}

// ── DEMO DATA ───────────────────────────────────

const DEMO_SUBSCRIPTIONS: CalendarSubscription[] = [
  {
    id: 'cal-sub-1', token: 'demo-feed-token-abc123',
    platform: 'google', isActive: true, createdAt: '2026-01-20T00:00:00Z',
  },
]

// ── COMPONENT ─────────────────────────────────────

export default function CalendarSyncModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const isEs = i18n.language === 'es'
  const [copied, setCopied] = useState(false)
  const [includeCompleted, setIncludeCompleted] = useState(false)

  const { data: apiSubs } = useQuery<CalendarSubscription[]>({
    queryKey: ['calendar-subs', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/calendar/subscriptions`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })
  const subscriptions = Array.isArray(apiSubs) ? apiSubs : (isDemoProject(projectId) ? DEMO_SUBSCRIPTIONS : [])

  const subscribeMutation = useMutation({
    mutationFn: async (platform: string) => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/calendar/subscribe`, {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform })
      }).then(r => r.json()) },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['calendar-subs', projectId] })
  })

  const deleteMutation = useMutation({
    mutationFn: async (subId: string) => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/calendar/subscriptions/${subId}`, { method: 'DELETE', headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }) },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['calendar-subs', projectId] })
  })

  const downloadUrl = `/api/projects/${projectId}/calendar/download${includeCompleted ? '?includeCompleted=true' : ''}`
  const activeSub = subscriptions?.find(s => s.isActive)
  const feedUrl = activeSub ? `${window.location.origin}/api/projects/${projectId}/calendar/feed/${activeSub.token}` : null

  const copyFeedUrl = () => {
    if (feedUrl) {
      navigator.clipboard.writeText(feedUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="p-5 max-w-4xl mx-auto space-y-4">
      <div>
        <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">{t('Calendar')}</h2>
        <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
          {t('Sync your project timeline with your phone')}
        </p>
      </div>

      {/* Download .ics */}
      <div className="bg-white border border-[#ECEAE4] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Download size={16} className="text-[#3B82F6]" />
          <h3 className="text-[13px] font-[800]">{t('Download Calendar')}</h3>
        </div>
        <p className="text-[10px] font-[600] text-[#9E9890] mb-3">
          {t('Download an .ics file to import into any calendar app')}
        </p>

        <label className="flex items-center gap-2 mb-3 cursor-pointer">
          <input type="checkbox" checked={includeCompleted} onChange={e => setIncludeCompleted(e.target.checked)}
            className="rounded border-[#ECEAE4]" />
          <span className="text-[11px] font-[600] text-[#9E9890]">{t('Include completed tasks')}</span>
        </label>

        <a href={downloadUrl} download
          className="block text-center bg-[#06539B] text-white text-[13px] font-[800] py-2.5 rounded-full hover:bg-[#14457E] transition-colors">
          <Download size={14} className="inline mr-1.5" />
          {t('Download .ics')}
        </a>
      </div>

      {/* Subscribe — live feed */}
      <div className="bg-white border border-[#ECEAE4] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Link2 size={16} className="text-[#14B8A6]" />
          <h3 className="text-[13px] font-[800]">{t('Subscribe to Calendar')}</h3>
        </div>
        <p className="text-[10px] font-[600] text-[#9E9890] mb-4">
          {t('Auto-updates on your phone when tasks change')}
        </p>

        {feedUrl ? (
          <div className="space-y-3">
            {/* Feed URL */}
            <div className="flex items-center gap-2 bg-[#F2F0EB] rounded-lg p-2.5">
              <span className="flex-1 text-[10px] font-[600] text-[#9E9890] truncate">{feedUrl}</span>
              <button onClick={copyFeedUrl} className="flex items-center gap-1 text-[10px] font-[800] text-[#3B82F6] flex-shrink-0">
                {copied ? <><Check size={12} /> {t('Copied')}</> : <><Copy size={12} /> {t('Copy')}</>}
              </button>
            </div>

            {/* Quick open buttons */}
            <div className="grid grid-cols-2 gap-2">
              <a href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(feedUrl.replace('https://', 'webcal://'))}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 border border-[#ECEAE4] rounded-xl py-3 text-[11px] font-[700] hover:bg-[#F2F0EB] transition-colors">
                <ExternalLink size={14} /> {t('Google Calendar')}
              </a>
              <a href={feedUrl.replace('https://', 'webcal://').replace('http://', 'webcal://')}
                className="flex items-center justify-center gap-2 border border-[#ECEAE4] rounded-xl py-3 text-[11px] font-[700] hover:bg-[#F2F0EB] transition-colors">
                <Smartphone size={14} /> {t('iPhone / Mac')}
              </a>
            </div>

            <div className="flex items-center gap-2 text-[9px] font-[700] text-[#22C55E]">
              <Check size={12} /> {t('Calendar synced — updates automatically')}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => subscribeMutation.mutate('google')}
              disabled={subscribeMutation.isPending}
              className="flex items-center justify-center gap-2 bg-[#06539B] text-white rounded-xl py-3 text-[11px] font-[800] disabled:opacity-40">
              {t('Google Calendar')}
            </button>
            <button onClick={() => subscribeMutation.mutate('apple')}
              disabled={subscribeMutation.isPending}
              className="flex items-center justify-center gap-2 bg-[#06539B] text-white rounded-xl py-3 text-[11px] font-[800] disabled:opacity-40">
              {t('iPhone / Mac')}
            </button>
          </div>
        )}
      </div>

      {/* Active subscriptions */}
      {subscriptions && subscriptions.length > 0 && (
        <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#ECEAE4]">
            <h3 className="text-[12px] font-[800] text-[#9E9890]">{t('Active Subscriptions')}</h3>
          </div>
          {subscriptions.map(sub => (
            <div key={sub.id} className="flex items-center gap-3 px-4 py-2.5 border-t border-[#F2F0EB] first:border-t-0">
              <CalendarDays size={14} className="text-[#9E9890]" />
              <span className="text-[11px] font-[700] flex-1">
                {sub.platform === 'google' ? 'Google Calendar' : sub.platform === 'apple' ? 'iPhone/Mac' : t('Calendar')}
              </span>
              <span className={`text-[9px] font-[800] px-2 py-0.5 rounded-full ${sub.isActive ? 'bg-green-50 text-green-600' : 'bg-[#F2F0EB] text-[#9E9890]'}`}>
                {sub.isActive ? t('Active') : t('Inactive')}
              </span>
              <button onClick={() => deleteMutation.mutate(sub.id)} className="p-1 hover:bg-red-50 rounded">
                <Trash2 size={12} className="text-[#9E9890]" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Instructions */}
      <div className="bg-[#F2F0EB] rounded-xl p-4">
        <h4 className="text-[11px] font-[800] text-[#1C1A17] mb-2">{t('How it works')}</h4>
        <ol className="space-y-1.5 text-[10px] font-[600] text-[#9E9890] list-decimal list-inside">
          <li>{t('Click "Subscribe" to generate a live calendar feed')}</li>
          <li>{t('Open the link in Google Calendar or iPhone')}</li>
          <li>{t('Your project timeline appears as calendar events')}</li>
          <li>{t('When you update tasks in bluaIQ, the calendar updates automatically')}</li>
        </ol>
      </div>
    </div>
  )
}
